import * as Sentry from "@sentry/react";

// GlitchTip (Sentry-compatible) error monitoring, imported first in main.tsx so
// its handlers wrap everything that follows.
//
// The DSN comes from VITE_SENTRY_DSN rather than being hardcoded the way the
// other apps do it, because this app needs its own GlitchTip project and one
// does not exist yet. Unset means no reporting, which is the right behaviour for
// a build that has not been given a project rather than a reason to fail.
const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;

// Gated on the hostname as well as PROD, not just PROD. A known standing problem
// on this estate is that locally-run production builds report into the live
// GlitchTip, which fills it with events from `localhost` and file paths under
// /Users/gaz. `import.meta.env.PROD` is true for any build, including a local
// `vite preview`, so the hostname is what actually separates the two.
const isLocalhost =
  typeof window !== "undefined" &&
  /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname);

export function initErrorReporting() {
  if (!dsn || !import.meta.env.PROD || isLocalhost) return;
  Sentry.init({
    dsn,
    tracesSampleRate: 0.01,
    denyUrls: [
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
      /^safari-(web-)?extension:\/\//i,
    ],
    // Errors thrown by drill code are the product working, not the app failing.
    // They are caught per test in the worker and reported in the UI, so nothing
    // here should ever see them, but the runner is a Worker and an unhandled
    // rejection inside it would otherwise surface as an app fault.
    ignoreErrors: [/__tests is not defined/i],
  });
}

/** Report a real app fault. Never used for a failing drill, which is normal output. */
export function reportError(error: unknown, context?: Record<string, unknown>) {
  if (!dsn || !import.meta.env.PROD || isLocalhost) return;
  Sentry.captureException(error, context ? { extra: context } : undefined);
}
